import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { BaseBlockView } from '../base-block.view';
import styled from 'styled-components';
import { HeroModel } from '../../../model/block';
import { inject, observer } from 'mobx-react';
import { IInjectedProps } from '../../../interface/component/injected-props.interface';
import { DraftToHtml } from '../../draft-to-html/draft-to-html';
import { getImageURL } from '../../../util/url/get-image-url.util';
import { action, computed, observable } from 'mobx';
import { SizeHelper } from '../../../util/style/size-helper';
import { getLinkFinalURL } from '../../../util/url/get-link-final-url';
import { draftToString } from '../../../util/draft/draft-to-string.util';
import { isDraftEmpty } from '../../../util/draft/is-draft-empty.util';
import { ArrowDownIcon, UploadImageIcon } from '../../icons';
import { IMAGE_HEIGHT, SMALL_SCREEN_SIZE } from '../../../constants';
import { CmsImage } from '../../cms-image/cms-image';
import { Carousel } from '../../carousel/carousel';
import { CmsButton } from '../../cms-button/cms-button';
import { largeMobileMedia } from '../../../util/style/large-mobile-media';
import { isStringEmpty } from '../../../util/string/is-string-empty.util';

interface IGradiendProps {
  color: string;
}

interface IButtonProps {
  color: string;
}

const ARROW_BUTTON_SIZE = 50;

const HeroWrapper = styled.div`
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
`;

const ArrowButton = styled.a`
  display: inline-block;
  border-radius: ${ARROW_BUTTON_SIZE / 2}px;
  height: ${ARROW_BUTTON_SIZE}px;
  width: ${ARROW_BUTTON_SIZE}px;
  position: absolute;
  bottom: 40px;
  outline:none;
  cursor: pointer;
  background: ${(props: IButtonProps) => props.color};
  z-index: 4;
  left: calc(50% - ${ARROW_BUTTON_SIZE / 2}px);
`;

const GradientRight = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 136px;
  background-image: linear-gradient(to bottom right, transparent 0%, transparent 50%, ${(props: IGradiendProps) => props.color} 0%, ${(props: IGradiendProps) => props.color} 50%);
  z-index: 2;
`;

const GradientLeft = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 65%;
  height: 160px;
  background-image: linear-gradient(to bottom left, transparent 0%, transparent 50%, ${(props: IGradiendProps) => props.color} 0%, ${(props: IGradiendProps) => props.color} 40%);
  z-index: 1;
`;

const CauroselContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 0;
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  will-change: transform;
`;
const HeroButtonsWrapper = styled.div`
  margin-top: 40px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const Header = styled.span`
  width: calc(100% - 190px);
  margin: 0 95px;
  ${largeMobileMedia(`
    width: 90%;
    margin: 0 5%;
   `)}
`;

const ImageContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  justify-content: center;
  align-items: center;
  display: flex;
  overflow: hidden;
  height: 100%;
  min-height: ${IMAGE_HEIGHT}px;
  width: 100%;
  margin: 0px;
  padding: 0px;
  flex: 0 0 50%;
`;

interface IHeroContainer {
  isInEditMode: boolean;
}

const HeroWrapperContainer = styled.div`
  position: relative;
  overflow: hidden;
  height: ${(props: IHeroContainer) => props.isInEditMode ? '400px' : SizeHelper.fullHeight(props)};
`;
const HeroScrollPosition = styled.div``;
const HERO_SCROLL_POSITION = 'hero-scroll-position';
const UPLOAD_IMAGE_SIZE = 50;

@inject('state', 'history')
@observer
export class HeroView extends BaseBlockView<HeroModel, IInjectedProps> {
  @observable mounted: boolean = false;
  heroOffset = 0;
  containerEl;
  removeScrollEvent;
  heroImageEl;

  scrollIntoView = () => {
    window.scrollTo({
      top: (document.getElementById(HERO_SCROLL_POSITION).getBoundingClientRect().top + window.document.documentElement.scrollTop),
      behavior: 'smooth'
    });
  }

  @computed
  get configuration() {
    return this.props.state.configuration;
  }

  componentWillUnmount() {
    if (this.removeScrollEvent) {
      this.removeScrollEvent();
      this.removeScrollEvent = undefined;
    }
  }

  setStyleToElement = (offset: number) => {
    if (!this.heroImageEl) {
      return;
    }
    const elementStyle = this.heroImageEl.style;
    const oldTransform = elementStyle.transform;
    const transformOffset = isStringEmpty(oldTransform) ? 0 : Number(oldTransform.split(',')[1].trim().slice(0, -2));
    const transform = `translate3d(0px, ${offset}px, 0px)`;
    let transition;
    if (Math.abs(offset - transformOffset) > 3) {
      transition = 'translate3d .2s';
    } else {
      transition = '';
    }
    elementStyle.transition = transition;
    elementStyle.MozTransition = transition;
    elementStyle.webkitTransition = transition;
    elementStyle.transform = transform;
    elementStyle.MozTransform = transform;
    elementStyle.webkitTransform = transform;
  }

  @action
  setHeroOffset = (offset: number) => {
    if (this.props.inEditor || !this.props.content.useParallax) {
      if (this.heroImageEl === 0) {
        return;
      }
      this.heroOffset = 0;
      this.setStyleToElement(0);
      return;
    }
    if (offset === this.heroOffset) {
      return;
    }
    this.heroOffset = offset;
    this.setStyleToElement(offset);
  }

  @action
  componentDidMount() {
    this.mounted = true;
    const containerPos = ReactDOM.findDOMNode(this.containerEl).getBoundingClientRect().top + (window.scrollY || window.pageYOffset || window.document.documentElement.scrollTop);
    const onScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset || window.document.documentElement.scrollTop;
      if (scrollY - containerPos > 0) {
        this.setHeroOffset((scrollY - containerPos) * this.props.content.parallaxAmount);
      } else {
        this.setHeroOffset(0);
      }
    };
    let timeout;
    const hasFrameFunctions = Boolean(requestAnimationFrame && cancelAnimationFrame);
    const timeoutFunction: (callback: () => void) => number = hasFrameFunctions ? (requestAnimationFrame as any) : (callback: () => void) => setTimeout(callback, 0);
    const cancelTimeoutFunction = hasFrameFunctions ? cancelAnimationFrame : clearTimeout;
    const executeTimeoutFunction = () => {
      onScroll();
      timeout = timeoutFunction(executeTimeoutFunction);
    };
    // needs to be vanilla javascript code for performance reasons, don't use observables here.
    let scrollAnimationTimeout;
    const removeScrollTimeout = () => {
      if (timeout) {
        cancelTimeoutFunction(timeout);
        timeout = undefined;
      }
      if (scrollAnimationTimeout) {
        clearTimeout(scrollAnimationTimeout);
        scrollAnimationTimeout = undefined;
      }
    };
    const scrollFunction = () => {
      if (scrollAnimationTimeout) {
        clearTimeout(scrollAnimationTimeout);
      } else {
        onScroll();
        executeTimeoutFunction();
      }
      scrollAnimationTimeout = setTimeout(removeScrollTimeout, 200);
    };
    if (window.addEventListener) {
      window.addEventListener('scroll', scrollFunction);
      this.removeScrollEvent = () => {
        window.removeEventListener('scroll', onScroll);
        removeScrollTimeout();
      };
    } else {
      window.onscroll = scrollFunction;
      this.removeScrollEvent = () => {
        window.onscroll = undefined;
        removeScrollTimeout();
      };
    }
    onScroll();

  }

  @computed
  get images() {
    const content = this.props.content;
    return content.images.map((currentImage, index) => {
      if (currentImage) {
        const imageURL = getImageURL(this.props.state, currentImage, 1000);
        if (imageURL) {
          return <CmsImage
            src={currentImage}
            key={index}
          />;
        }
      }
    }).filter(img => Boolean(img));
  }

  render() {
    const isMobile = this.props.state.configuration.isMobile || this.props.state.windowSize.width < SMALL_SCREEN_SIZE;
    const { content } = this.props;
    const { buttonLeft, buttonRight, mobileMainHeader, mobileSecondaryHeader, bottomColorLeft, arrowBackgroundColor, bottomColorRight, arrowColor, secondaryHeader, mainHeader, images, leftButtonColor, rightButtonColor } = content;
    let header = mainHeader;
    let secondHeader = secondaryHeader;
    if (isMobile) {
      header = isDraftEmpty(mobileMainHeader) ? header : mobileMainHeader;
      secondHeader = isDraftEmpty(mobileSecondaryHeader) ? secondHeader : mobileSecondaryHeader;
    }
    const carousel = <CauroselContainer
      ref={(heroImage) => this.heroImageEl = ReactDOM.findDOMNode(heroImage)}
    >
      {content.isVideo && content.video.location !== '' && !isMobile ?
        <video autoPlay={true} loop={true} id="myvid" muted={true} key={content.video.location} style={{ minWidth: '100%' }}>
          <source type="video/mp4" src={content.video.location} />
          Your browser does not support HTML5 video.
        </video> :
        <Carousel
          images={this.images}
          autoLoop={true}
          wrapper={ImageContainer as any}
          transitionDuration={this.props.content.transitionDuration}
          transitionTime={this.props.content.transitionTime}
          disableIndicators={true}
        />}
    </CauroselContainer>;
    return (
      <HeroWrapperContainer
        ref={(container) => this.containerEl = container}
        isInEditMode={this.props.inEditor}
      >
        {carousel}
        <HeroWrapper data-type="HeroView">
          {((!images || images.length === 0) && this.props.inEditor) &&
            <UploadImageIcon style={{ width: UPLOAD_IMAGE_SIZE, height: UPLOAD_IMAGE_SIZE }} />}
          {header && <Header><DraftToHtml
            draft={header}
            inEditor={this.props.inEditor}
          /></Header>}
          {secondHeader && <Header><DraftToHtml
            draft={secondHeader}
            inEditor={this.props.inEditor}
          /></Header>}
          <HeroButtonsWrapper>
            {buttonLeft && !isDraftEmpty(buttonLeft.text, this.props.state) &&
              <CmsButton
                to={getLinkFinalURL(this.props.state, buttonLeft.link)}
                text={draftToString(buttonLeft.text, this.props.state)}
                color={leftButtonColor}
              />}
            {buttonRight && !isDraftEmpty(buttonRight.text, this.props.state) &&
              <CmsButton
                to={getLinkFinalURL(this.props.state, buttonRight.link)}
                color={rightButtonColor}
                text={draftToString(buttonRight.text, this.props.state)}
              />}
          </HeroButtonsWrapper>
          {content.colorStyling === 'on' && <GradientLeft color={this.props.state.colors.getColor(bottomColorLeft)} />}
          {content.colorStyling === 'on' && <GradientRight color={this.props.state.colors.getColor(bottomColorRight)} />}
          <ArrowButton
            onClick={this.scrollIntoView}
            color={this.props.state.colors.getColor(arrowBackgroundColor)}
          >
            <ArrowDownIcon color={this.props.state.colors.getColor(arrowColor)} />
          </ArrowButton>
        </HeroWrapper>
        <HeroScrollPosition id={HERO_SCROLL_POSITION} />
      </HeroWrapperContainer>
    );
  }
}
